import React, { useRef, useState, useEffect } from 'react';
import 'react-circular-progressbar/dist/styles.css';
import './cart.scss';
import axios from 'axios';
import {
  Payment,
  CallSplit,
  MergeType,
  Close,
  CheckCircle,
  Error,
  Print,
  AccessTime,
  CreditCard,
  AttachMoney,
  LocalDining,
  TableRestaurant,
  Schedule,
  PointOfSale,
  ReceiptLong,
  MonetizationOn,
  Balance,
  ArrowDownward,
  FormatListBulleted
} from '@mui/icons-material';

const Cart = ({ tableId }) => {
  const componentRef = useRef();
  const [orderDetails, setOrderDetails] = useState(null);
  const [updateStatus, setUpdateStatus] = useState('');
  const [updatePendingOrderStatus, setUpdatePendingOrderStatus] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [showSplitDialog, setSplitShowDialog] = useState(false);
  const [showCombineDialog, setCombineShowDialog] = useState(false);
  const [splitUpdateShowDialog, setSplitUpdateShowDialog] = useState(false);
  const [cashAmount, setCashAmount] = useState('');
  const [cashSingleOrderAmount, setCashSingleOrderAmount] = useState('');
  const [balance, setBalance] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const apiUrl = process.env.REACT_APP_API_URL;
  const [pendingOrders, setPendingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [combinedBill, setCombinedBill] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const openSplitUpdateDialog = (orderId) => {
    setSelectedOrderId(orderId);
    setSplitUpdateShowDialog(true);
  };

  const closeSplitUpdateDialog = () => {
    setSplitUpdateShowDialog(false);
    setSelectedOrderId(null);
  };

  const openPaymentDialog = (orderData) => {
    if (showCombineDialog) closeCombineDialog();
    if (showSplitDialog) closeSplitDialog();

    setOrderDetails(orderData);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
  };

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        if (!tableId) {
          console.error('Table ID is not provided');
          return;
        }

        const storedToken = localStorage.getItem('accessToken');
        const response = await fetch(`${apiUrl}/orders/tableId?tableId=${tableId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            token: `Bearer ${storedToken}`,
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        console.log('Fetched order details:', data);

        if (Array.isArray(data) && data.length > 0 && data[0].isTableAvailable) {
          setOrderDetails(data[0]);
        } else {
          setOrderDetails(null);
        }
      } catch (error) {
        console.error('Error fetching order details:', error);
      }
    };

    if (tableId) {
      fetchOrderDetails();
    }
  }, [tableId, apiUrl]);

  const openCombineDialog = async () => {
    try {
      const storedToken = localStorage.getItem('accessToken');
      const response = await fetch(`${apiUrl}/orders/combine/tableId?tableId=${tableId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${storedToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Fetched combined bill:', data);

      if (data && data.isTableAvailable) {
        setCombinedBill(data);
        setCombineShowDialog(true);
      } else {
        console.error('Combined bill not found');
      }
    } catch (error) {
      console.error('Error fetching combined bill:', error);
    }
  };

  useEffect(() => {
    if (orderDetails && cashAmount !== '') {
      const total = orderDetails.grandTotal;
      setGrandTotal(total);
      const cash = parseFloat(cashAmount);
      setBalance(cash - total);
    }
  }, [cashAmount, orderDetails]);

  const closeCombineDialog = () => {
    setCombineShowDialog(false);
  };

  const closeSplitDialog = () => {
    setSplitShowDialog(false);
  };

  const handleCashChange = (e) => {
    setCashAmount(e.target.value);
  };

  const handleCombineBillPayment = async () => {
    console.log('Checking the tableID:', tableId);
    try {
      const storedToken = localStorage.getItem('accessToken');
      console.log('Retrieved access token:', storedToken);

      const data = {
        status: 'paid',
        isTableAvailable: false,
      };

      const config = {
        method: 'put',
        url: `${apiUrl}/orders/combine/${tableId}`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${storedToken}`,
        },
        data: data,
      };

      const response = await axios.request(config);
      console.log('Response data:', response.data);
      setUpdateStatus('success');

      setTimeout(() => {
        closeDialog();
      }, 2000);
    } catch (error) {
      console.error('Error updating order status:', error.response ? error.response.data : error.message);
      setUpdateStatus('error');
    }
  };

  const handleSplitBill = async () => {
    setLoading(true);

    try {
      if (!tableId) {
        console.error('Table ID is not provided');
        return;
      }

      const storedToken = localStorage.getItem('accessToken');
      const response = await fetch(`${apiUrl}/orders/pending/tableId?tableId=${tableId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${storedToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      setSplitShowDialog(true);
      console.log('Fetched pending new orders:', data);
      setPendingOrders(data);
    } catch (error) {
      console.error('Error fetching pending orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePendingBillPayment = async (orderId) => {
    console.log('Checking the tableID:', tableId);
    console.log('Checking the Order ID:', orderId);
    try {
      const storedToken = localStorage.getItem('accessToken');
      console.log('Retrieved access token:', storedToken);

      const data = {
        status: 'paid',
        isTableAvailable: false,
      };

      const config = {
        method: 'put',
        url: `${apiUrl}/orders/pending/${orderId}`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${storedToken}`,
        },
        data: data,
      };

      const response = await axios.request(config);
      console.log('Response data:', response.data);
      setUpdatePendingOrderStatus('success');

      setTimeout(() => {
        closeSplitUpdateDialog();
      }, 2000);
    } catch (error) {
      console.error('Error updating order status:', error.response ? error.response.data : error.message);
      setUpdatePendingOrderStatus('error');
    }
  };

  const handleCashSingleOrderChange = (e) => {
    setCashSingleOrderAmount(e.target.value);
  };

  return (
    <div className="cart-container">
      {updateStatus === 'success' && (
        <div className="alert success">
          <CheckCircle className="alert-icon" /> Payment successful!
        </div>
      )}
      {updateStatus === 'error' && (
        <div className="alert error">
          <Error className="alert-icon" /> Error updating order. Please try again.
        </div>
      )}

      {orderDetails ? (
        <div className="cart-content">
          <div className="order-summary-card" ref={componentRef}>
            <div className="receipt-header">
              <div className="merchant-info">
                <h2>{orderDetails.merchantId.username}</h2>
                <p className="address"><LocalDining className="info-icon" /> {orderDetails.merchantId.address}</p>
                <p className="phone"><PointOfSale className="info-icon" /> {orderDetails.merchantId.phone}</p>
              </div>
              <div className="order-meta">
                <div className="meta-item">
                  <span className="meta-label">Order ID:</span>
                  <span className="meta-value">{orderDetails._id}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Table No:</span>
                  <span className="meta-value highlight"><TableRestaurant className="table-icon" /> {orderDetails.tableId.tableno}</span>
                </div>
                <div className="meta-item">
                  <span className="meta-label">Created:</span>
                  <span className="meta-value"><Schedule className="time-icon" /> {new Date(orderDetails.createdAt).toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="items-section">
              <h3 className="section-title"><FormatListBulleted className="section-icon" /> Order Items</h3>
              {orderDetails.foods && Array.isArray(orderDetails.foods) ? (
                <div className="items-list">
                  {orderDetails.foods.map((food, index) => (
                    <div key={index} className="item-row">
                      <div className="item-main">
                        <span className="item-quantity">{food.quantity}x</span>
                        <span className="item-name">{food.foodId.title}</span>
                        <span className="item-price">RM {food.foodId.price.toFixed(2)}</span>
                      </div>
                      {food.requests && Array.isArray(food.requests) && food.requests.length > 0 && (
                        <div className="item-addons">
                          {food.requests.map((request, reqIndex) => (
                            <div key={reqIndex} className="addon-item">
                              <span className="addon-name"><ArrowDownward className="addon-icon" /> {request.name}</span>
                              <span className="addon-price">+RM {request.addOnPrice.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-items">No food items found</p>
              )}
            </div>

            <div className="totals-section">
              <div className="total-row">
                <span>Sub Total:</span>
                <span>RM {orderDetails.subtotal.toFixed(2)}</span>
              </div>
              <div className="total-row">
                <span>SST (8%):</span>
                <span>RM {(orderDetails.amount * 0.08).toFixed(2)}</span>
              </div>
              <div className="total-row">
                <span>Service Charge (10%):</span>
                <span>RM {(orderDetails.amount * 0.1).toFixed(2)}</span>
              </div>
              <div className="total-row grand-total">
                <span>Grand Total:</span>
                <span>RM {orderDetails.grandTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="action-buttons">
            {orderDetails.isTableAvailable && (
              <>
                <button className="btn-primary" onClick={() => openPaymentDialog(orderDetails)}>
                  <Payment className="btn-icon" /> Process Payment
                </button>
                <button className="btn-secondary" onClick={openCombineDialog}>
                  <MergeType className="btn-icon" /> Combine Bill
                </button>
                <button className="btn-secondary" onClick={handleSplitBill}>
                  <CallSplit className="btn-icon" /> Split Bill
                </button>
              </>
            )}
            {!orderDetails.isTableAvailable && (
              <div className="no-order-message">
                <p>No active order for this table</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="no-order-state">
          <p>{tableId ? 'No order found for the specified table' : 'Table ID is not provided'}</p>
        </div>
      )}

      {showDialog && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2><CreditCard className="modal-icon" /> Payment Details</h2>
              <button className="close-btn" onClick={closeDialog}>
                <Close />
              </button>
            </div>

            <div className="modal-body">
              {updateStatus === 'success' && (
                <div className="success-message">
                  <CheckCircle className="success-icon" /> Payment successful!
                </div>
              )}
              {updateStatus === 'error' && (
                <div className="error-message">
                  <Error className="error-icon" /> Error processing payment
                </div>
              )}

              <div className="payment-summary">
                <div className="amount-row">
                  <span>Order Total:</span>
                  <span>RM {orderDetails?.grandTotal?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="amount-row">
                  <span>Grand Total:</span>
                  <span className="highlight">RM {orderDetails?.grandTotal?.toFixed(2) || '0.00'}</span>
                </div>
              </div>

              <div className="cash-input-group">
                <label htmlFor="cashAmount">
                  <AttachMoney className="input-icon" /> Cash Received
                </label>
                <input
                  type="number"
                  id="cashAmount"
                  value={cashAmount}
                  onChange={handleCashChange}
                  placeholder="Enter cash amount"
                  className="cash-input"
                />
              </div>

              {cashAmount && (
                <div className="balance-display">
                  <div className="balance-row">
                    <span>Change Due:</span>
                    <span className={`balance-amount ${balance >= 0 ? 'positive' : 'negative'}`}>
                      <Balance className="balance-icon" /> RM {balance.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeDialog}>
                Cancel
              </button>
              <button
                className={`btn-confirm ${parseFloat(cashAmount) >= grandTotal ? '' : 'disabled'}`}
                onClick={handleCombineBillPayment}
                disabled={parseFloat(cashAmount) < grandTotal}
              >
                Confirm Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {showCombineDialog && (
        <div className="modal-overlay">
          <div className="modal wide-modal">
            <div className="modal-header">
              <h2><ReceiptLong className="modal-icon" /> Combined Bill</h2>
              <button className="close-btn" onClick={closeCombineDialog}>
                <Close />
              </button>
            </div>

            <div className="modal-body">
              {combinedBill ? (
                <div className="combined-bill">
                  <div className="bill-header">
                    <h3>{combinedBill.merchantId.username}</h3>
                    <div className="bill-meta">
                      <p><strong><TableRestaurant className="meta-icon" /> Table No:</strong> {combinedBill.tableId.tableno}</p>
                      <p><strong><Schedule className="meta-icon" /> Created:</strong> {new Date(combinedBill.updatedAt).toLocaleString()}</p>
                    </div>
                  </div>

                  <div className="items-container">
                    <h4><FormatListBulleted className="section-icon" /> Items Ordered:</h4>
                    <div className="scrollable-items">
                      {combinedBill.foods.map((food) => (
                        <div key={food._id} className="combined-item">
                          <div className="item-header">
                            <span className="quantity">{food.quantity}x</span>
                            <span className="name">{food.title}</span>
                            <span className="price">RM {food.price.toFixed(2)}</span>
                          </div>
                          {food.requests && food.requests.length > 0 && (
                            <div className="addons">
                              {food.requests.map((request) => (
                                <div key={request._id} className="addon">
                                  <span><ArrowDownward className="addon-icon" /> {request.name}</span>
                                  <span>+RM {request.addOnPrice.toFixed(2)}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="combined-totals">
                    <div className="total-row">
                      <span>Subtotal:</span>
                      <span>RM {combinedBill.subtotal.toFixed(2)}</span>
                    </div>
                    <div className="total-row">
                      <span>SST:</span>
                      <span>RM {combinedBill.sst.toFixed(2)}</span>
                    </div>
                    <div className="total-row">
                      <span>Service Charge:</span>
                      <span>RM {combinedBill.serviceCharge.toFixed(2)}</span>
                    </div>
                    <div className="total-row">
                      <span>Round Up:</span>
                      <span>RM {combinedBill.adjustment.toFixed(2)}</span>
                    </div>
                    <div className="total-row grand-total">
                      <span>Grand Total:</span>
                      <span>RM {combinedBill.grandTotal.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              ) : (
                <p className="no-data">No combined orders available.</p>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeCombineDialog}>
                Close
              </button>
              <button
                className="btn-primary"
                onClick={() => openPaymentDialog(combinedBill)}
              >
                <Print className="btn-icon" /> Print & Process Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {splitUpdateShowDialog && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2><CreditCard className="modal-icon" /> Single Order Payment</h2>
              <button className="close-btn" onClick={closeSplitUpdateDialog}>
                <Close />
              </button>
            </div>

            <div className="modal-body">
              {updatePendingOrderStatus === 'success' && (
                <div className="success-message">
                  <CheckCircle className="success-icon" /> Payment successful!
                </div>
              )}
              {updatePendingOrderStatus === 'error' && (
                <div className="error-message">
                  <Error className="error-icon" /> Error processing payment
                </div>
              )}

              <div className="order-info">
                <p><strong>Order ID:</strong> {selectedOrderId}</p>
                <div className="amount-display">
                  <span>Amount Due:</span>
                  <span className="amount">RM {orderDetails?.grandTotal?.toFixed(2) || '0.00'}</span>
                </div>
              </div>

              <div className="cash-input-group">
                <label htmlFor="cashSingleOrderAmount">
                  <MonetizationOn className="input-icon" /> Cash Received
                </label>
                <input
                  type="number"
                  id="cashSingleOrderAmount"
                  value={cashSingleOrderAmount}
                  onChange={handleCashSingleOrderChange}
                  placeholder="Enter cash amount"
                  className="cash-input"
                />
              </div>

              {cashSingleOrderAmount && (
                <div className="balance-display">
                  <span>Change:</span>
                  <span className={`balance ${parseFloat(cashSingleOrderAmount) >= parseFloat(orderDetails?.grandTotal || 0) ? 'positive' : 'negative'}`}>
                    RM {(parseFloat(cashSingleOrderAmount || 0) - parseFloat(orderDetails?.grandTotal || 0)).toFixed(2)}
                  </span>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeSplitUpdateDialog}>
                Cancel
              </button>
              <button
                className={`btn-confirm ${parseFloat(cashSingleOrderAmount || 0) >= parseFloat(orderDetails?.grandTotal || 0) ? '' : 'disabled'}`}
                onClick={() => handlePendingBillPayment(selectedOrderId)}
                disabled={parseFloat(cashSingleOrderAmount || 0) < parseFloat(orderDetails?.grandTotal || 0)}
              >
                Process Payment
              </button>
            </div>
          </div>
        </div>
      )}

      {showSplitDialog && (
        <div className="modal-overlay">
          <div className="modal wide-modal">
            <div className="modal-header">
              <h2><CallSplit className="modal-icon" /> Split Bill - Pending Orders</h2>
              <button className="close-btn" onClick={closeSplitDialog}>
                <Close />
              </button>
            </div>

            <div className="modal-body">
              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Loading pending orders...</p>
                </div>
              ) : (
                <div className="pending-orders-grid">
                  {pendingOrders.map((order) => (
                    <div className="order-card" key={order.orderId}>
                      <div className="order-card-header">
                        <h3>Order #{order.orderId}</h3>
                        <span className="table-badge"><TableRestaurant className="badge-icon" /> Table {order.tableId.tableno}</span>
                      </div>

                      <div className="order-card-body">
                        <div className="order-time">
                          <AccessTime className="time-icon" /> {new Date(order.updatedAt).toLocaleString()}
                        </div>

                        <div className="order-items-preview">
                          <h4><FormatListBulleted className="section-icon" /> Items:</h4>
                          <div className="items-list">
                            {order.foods && Array.isArray(order.foods) ? (
                              <>
                                {order.foods.slice(0, 3).map((food, index) => (
                                  <div key={index} className="preview-item">
                                    <span>{food.quantity}x {food.foodId.title}</span>
                                    <span>RM {food.foodId.price.toFixed(2)}</span>
                                  </div>
                                ))}
                                {order.foods.length > 3 && (
                                  <div className="more-items">
                                    +{order.foods.length - 3} more items
                                  </div>
                                )}
                              </>
                            ) : (
                              <p>No items</p>
                            )}
                          </div>
                        </div>

                        <div className="order-summary">
                          <div className="summary-row">
                            <span>Subtotal:</span>
                            <span>RM {order.subtotal.toFixed(2)}</span>
                          </div>
                          <div className="summary-row">
                            <span>SST (8%):</span>
                            <span>RM {(order.amount * 0.08).toFixed(2)}</span>
                          </div>
                          <div className="summary-row">
                            <span>Service (10%):</span>
                            <span>RM {(order.amount * 0.1).toFixed(2)}</span>
                          </div>
                          <div className="summary-row total">
                            <span>Total:</span>
                            <span>RM {order.grandTotal.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="order-card-footer">
                        <button
                          className="btn-primary"
                          onClick={() => {
                            closeSplitDialog();
                            openSplitUpdateDialog(order.orderId);
                          }}
                        >
                          <Payment className="btn-icon" /> Process Payment
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={closeSplitDialog}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cart;